import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import {
  ItemLink,
  ItemIcon,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemAction,
} from '@dashboard/components/ui/item';
import { fromZonedTime } from 'date-fns-tz';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { PageProvider } from '@dashboard/hooks/usePage';
import { confirm } from '@dashboard/lib/confirm';
import getGmtOffsetForTimezone from '@dashboard/services/getGmtOffsetForTimezone';
import { toast } from '@dashboard/context/ToastContext';
import logException from '@dashboard/services/logException';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import storage from '@dashboard/services/storage';
import timezones from '@dashboard/services/timezones';
import Button from '@dashboard/components/Button';
import { Form } from '@dashboard/components/Formik';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import { Card } from '@dashboard/components/ui/card';
import { StepDots } from '@dashboard/components/ui/step-dots';
import Logo from '@dashboard/components/Logo';
import StepTransition from '@dashboard/components/StepTransition';
import { isGenericEmailDomain } from '@dashboard/utils/emailDomain';
import {
  TicketGroupEditProvider,
  useTicketGroupEdit,
} from '../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/TicketGroupEditContext';
import {
  TicketGroupsProvider,
  useTicketGroups,
} from '../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/TicketGroupsContext';
import ProductDetailsForm from '../PageEdit/scenes/PageProduct/components/ProductDetailsForm';
import TicketGroupsList from '../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/components/TicketGroupsList';
import AddTicketGroupDialog from '../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/components/AddTicketGroupDialog';
import { upperFirst } from 'lodash';

function getStepCount(pageType) {
  switch (pageType) {
    case 'event':
      return 3;
    case 'form':
      return 1;
    default:
      return 2;
  }
}

function getUrlType(pathname) {
  if (pathname.startsWith('/checkouts/new')) return PAGE_TYPES.CHECKOUT;
  if (pathname.startsWith('/events/new')) return PAGE_TYPES.EVENT;
  if (pathname.startsWith('/forms/new')) return PAGE_TYPES.FORM;
  return null;
}

function getBackHref(urlType) {
  switch (urlType) {
    case PAGE_TYPES.CHECKOUT:
      return '/checkouts';
    case PAGE_TYPES.EVENT:
      return '/events';
    case PAGE_TYPES.FORM:
      return '/forms';
    default:
      return '/checkouts';
  }
}

const PageNew = () => {
  const location = useLocation();
  const history = useHistory();
  const { user, seller } = useAuth();
  const { updatePage } = usePageQueries();
  const isOnboarding = new URLSearchParams(location.search).get('onboarding') === '1';
  const urlType = useMemo(() => getUrlType(location.pathname), [location.pathname]);
  const lastCreatedPageType = storage.local.getItem('last-created-page-type');
  const [selectedType, setSelectedType] = useState(urlType || lastCreatedPageType || null);
  const [currentStep, setCurrentStep] = useState(urlType && urlType !== PAGE_TYPES.FORM ? 2 : 1);
  const [tempPage, setTempPage] = useState({});
  const backHref = getBackHref(urlType);

  const isGenericEmail = useMemo(() => isGenericEmailDomain(user?.email), [user?.email]);
  const isStripeConnected = checkIsConnectedToStripe(seller);
  const onboardingStepCount = isOnboarding ? (isGenericEmail ? 6 : 5) : 0;

  // Auto-create a form when landing on /forms/new — matches the existing
  // "click Form in the type picker" behavior which skips straight to the editor.
  const autoCreatedFormRef = useRef(false);
  useEffect(() => {
    if (urlType !== PAGE_TYPES.FORM || autoCreatedFormRef.current) return;
    autoCreatedFormRef.current = true;

    (async () => {
      try {
        const lastHostedLayoutType = storage.local.getItem('last-hosted-layout-type');
        const lastUsageType = storage.local.getItem('last-usage-type');
        const lastSiteLayoutType = storage.local.getItem('last-site-layout-type');
        const layoutDefaults = {
          ...(lastHostedLayoutType && { hostedLayoutType: lastHostedLayoutType }),
          ...(lastUsageType && { usageType: lastUsageType }),
          ...(lastSiteLayoutType && { siteLayoutType: lastSiteLayoutType }),
        };

        const { data: page, error } = await updatePage({
          name: 'Untitled form',
          title: '',
          type: 'form',
          ...layoutDefaults,
        });
        if (error) throw error;

        storage.local.setItem('last-created-page-type', PAGE_TYPES.FORM);
        toast({ title: 'Form created', type: 'success' });
        history.push(`/pages/${page._id}/customize`);
      } catch (error) {
        logException({ message: 'Failed to create form', error });
        toast({
          title: 'Unable to create form',
          description: 'Please try again.',
          type: 'error',
        });
        history.push('/forms');
      }
    })();
  }, [urlType, updatePage, history]);

  function onCreatePage(page, { tab } = {}) {
    if (page.type === PAGE_TYPES.FORM || page.type === PAGE_TYPES.CHECKOUT) {
      toast({
        title: `${upperFirst(page.type)} created`,
        type: 'success',
      });
      history.push(`/pages/${page._id}/customize${tab ? `/${tab}` : ''}`);
      return;
    }

    setTempPage(page);
    setNextStep();
  }

  function setNextStep() {
    setCurrentStep(currentStep + 1);
    window.scrollTo(0, 0);
  }

  function setPreviousStep() {
    // When URL-scoped there is no type-picker step to return to — go back to the list.
    if (urlType && currentStep === 2) {
      history.push(backHref);
      return;
    }
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  }

  return (
    <div>
      {isOnboarding ? (
        <div className="absolute top-0 left-0 p-2">
          <div className="flex h-10 items-center pl-2">
            <Logo size={20} />
          </div>
        </div>
      ) : (
        <div className="md:mt-6 pt-0.5 ml-4 absolute top-0">
          <Button appearance="light" to={backHref}>
            <ChevronLeftIcon className="w-5 h-5" />
          </Button>
        </div>
      )}

      <div className="flex flex-col items-center pt-[15vh] px-4">
        <div className="w-full max-w-2xl">
          {urlType === PAGE_TYPES.FORM ? null : (
            <PageProvider pageId={tempPage._id} initialData={tempPage}>
              <StepTransition stepKey={currentStep}>
                <PageNewSteps
                  currentStep={currentStep}
                  selectedType={selectedType}
                  setSelectedType={setSelectedType}
                  setNextStep={setNextStep}
                  isOnboarding={isOnboarding}
                  setPreviousStep={setPreviousStep}
                  tempPage={tempPage}
                  onCreatePage={onCreatePage}
                  showBack={!urlType}
                />
              </StepTransition>
            </PageProvider>
          )}
        </div>
      </div>

      <div className="my-8 flex justify-center md:fixed md:bottom-8 md:left-0 md:right-0 md:my-0">
        <StepDots
          total={onboardingStepCount + getStepCount(selectedType)}
          current={onboardingStepCount + (currentStep - 1)}
          maxClickable={onboardingStepCount}
          onNavigate={i => {
            if (i < onboardingStepCount) {
              let step = i + 1;
              if (step === onboardingStepCount && isStripeConnected) {
                step = step - 1;
              }
              history.push(`/seller/start?step=${step}`);
            }
          }}
        />
      </div>
    </div>
  );
};

function PageNewSteps({
  currentStep,
  selectedType,
  setSelectedType,
  setNextStep,
  isOnboarding,
  setPreviousStep,
  tempPage,
  onCreatePage,
  showBack,
}) {
  const history = useHistory();

  return (
    <div>
      {currentStep === 1 && (
        <SelectPageType
          setSelectedType={setSelectedType}
          setNextStep={setNextStep}
          isOnboarding={isOnboarding}
          onCreatePage={onCreatePage}
        />
      )}

      {currentStep === 2 && (
        <CreatePage
          page={tempPage}
          onCreatePage={onCreatePage}
          pageType={selectedType}
          isOnboarding={isOnboarding}
          onBack={setPreviousStep}
          showBack={showBack}
        />
      )}

      {tempPage.type === PAGE_TYPES.EVENT && currentStep === 3 && (
        <CreateTicketTypes
          setNextStep={() => {
            toast({
              title: 'Event created',
              type: 'success',
            });
            history.push(`/pages/${tempPage._id}/customize`);
          }}
        />
      )}
    </div>
  );
}

const PAGE_TYPE_OPTIONS = [
  {
    value: 'checkout',
    label: 'Checkout',
    description: 'Sell products and subscriptions',
    icon: CreditCardIcon,
  },
  {
    value: 'event',
    label: 'Event',
    description: 'Sell tickets and check-in guests',
    icon: CalendarIcon,
  },
  {
    value: 'form',
    label: 'Form',
    description: 'Capture leads and collect feedback',
    icon: ClipboardDocumentCheckIcon,
  },
];

const cardClassName =
  'w-full rounded-lg border border-gray-200 dark:border-border p-4 min-h-[80px] outline-none focus-visible:ring-2 focus-visible:ring-gray-950 dark:focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-gray-100 dark:active:bg-muted';

const SelectPageType = ({ setSelectedType, setNextStep, isOnboarding, onCreatePage }) => {
  const { updatePage } = usePageQueries();
  const lastHostedLayoutType = storage.local.getItem('last-hosted-layout-type');
  const lastUsageType = storage.local.getItem('last-usage-type');
  const lastSiteLayoutType = storage.local.getItem('last-site-layout-type');
  const layoutDefaults = {
    ...(lastHostedLayoutType && { hostedLayoutType: lastHostedLayoutType }),
    ...(lastUsageType && { usageType: lastUsageType }),
    ...(lastSiteLayoutType && { siteLayoutType: lastSiteLayoutType }),
  };

  const handleSelectType = async option => {
    setSelectedType(option.value);
    storage.local.setItem('last-created-page-type', option.value);

    if (option.value === 'form') {
      try {
        const { data: page, error } = await updatePage({
          name: 'Untitled form',
          title: '',
          type: 'form',
          ...layoutDefaults,
        });

        if (error) throw error;

        onCreatePage(page);
      } catch (error) {
        logException({
          message: 'Failed to create form',
          error,
        });
        toast({
          title: 'Unable to create form',
          description: 'Please try again.',
          type: 'error',
        });
      }
    } else {
      setNextStep();
    }
  };

  return (
    <div className="mb-36">
      <div className="mx-auto max-w-lg">
        {isOnboarding ? (
          <div className="mb-8 text-center space-y-1">
            <h1 className="text-xl text-balance">Let's create your first page</h1>
            <p className="text-muted-foreground text-sm">What type of page do you need?</p>
          </div>
        ) : (
          <div className="mb-8 text-center space-y-1">
            <h1 className="text-xl text-balance">Page type</h1>
            <p className="text-muted-foreground text-sm">Create a checkout page, event or form</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {PAGE_TYPE_OPTIONS.map(option => {
            const Icon = option.icon;
            return (
              <ItemLink
                key={option.value}
                asChild
                onClick={() => handleSelectType(option)}
                className={cardClassName}
              >
                <button type="button">
                  <ItemIcon className="[&>svg]:size-6 size-6 text-gray-600 dark:text-muted-foreground self-center">
                    <Icon />
                  </ItemIcon>
                  <ItemContent>
                    <ItemTitle className="">{option.label}</ItemTitle>
                    <ItemDescription className="">{option.description}</ItemDescription>
                  </ItemContent>
                  <ItemAction>
                    <ChevronRightIcon />
                  </ItemAction>
                </button>
              </ItemLink>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CreatePage = ({ onCreatePage, pageType, onBack, showBack }) => {
  const { updatePage } = usePageQueries();

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const lastHostedLayoutType = storage.local.getItem('last-hosted-layout-type');
  const lastUsageType = storage.local.getItem('last-usage-type');
  const lastSiteLayoutType = storage.local.getItem('last-site-layout-type');
  const layoutDefaults = {
    ...(lastHostedLayoutType && { hostedLayoutType: lastHostedLayoutType }),
    ...(lastUsageType && { usageType: lastUsageType }),
    ...(lastSiteLayoutType && { siteLayoutType: lastSiteLayoutType }),
  };

  return (
    <>
      <div className="mb-36">
        <div className="mx-auto max-w-lg">
          <div className="mb-8 text-center relative space-y-1">
            {showBack && (
              <div className="absolute left-0 top-0 bottom-0 flex items-center">
                <button
                  type="button"
                  onClick={onBack}
                  className="text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-muted-foreground transition-colors"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
              </div>
            )}
            <h1 className="text-xl text-balance">
              {pageType === 'checkout' && 'Checkout page'}
              {pageType === 'event' && 'Create event'}
              {pageType === 'form' && 'Form'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {pageType === 'checkout' && 'Add details about your product'}
              {pageType === 'event' && 'Add details about your event'}
              {pageType === 'form' && 'Add details about your form'}
            </p>
          </div>

          {pageType === 'checkout' && (
            <Card className="p-5">
              <ProductDetailsForm
                isNew
                onChange={async (product, { variantsPriced } = {}) => {
                  try {
                    const { data: page, error } = await updatePage({
                      type: 'checkout',
                      name: product.title,
                      product: product._id,
                      productTitle: product.title,
                      productType: product.type,
                      ...layoutDefaults,
                    });

                    if (error) throw error;

                    // TODO(pricing-type-revival): only an explicit "no base price"
                    // choice lands on the variants tab; a product that merely ends
                    // up no-base-price (a blank/0 draft) opens the normal editor.
                    onCreatePage(page, variantsPriced ? { tab: 'product/variants' } : {});
                  } catch (error) {
                    console.log(error);
                    logException({
                      message: 'Failed to create checkout',
                      error,
                    });
                    toast({
                      title: 'Unable to create checkout. Please try again.',
                      type: 'error',
                    });
                  }
                }}
              />
            </Card>
          )}

          {pageType === 'event' && (
            <Card className="p-5">
              <Form
                initialValues={{
                  title: '',
                  eventDetails: {
                    currency: 'usd',
                    startDate: null,
                    endDate: null,
                    type: 'in_person',
                    timezone: userTimezone,
                    location: '',
                    mapsLink: '',
                    capacity: '',
                    status: 'active',
                  },
                }}
                onSubmit={async values => {
                  try {
                    const eventDetails = {
                      ...values.eventDetails,
                    };

                    if (values.eventDetails.platform === '') {
                      eventDetails.platform = undefined;
                    }

                    if (values.eventDetails.startDate) {
                      eventDetails.startDate = fromZonedTime(
                        values.eventDetails.startDate,
                        values.eventDetails.timezone,
                      );
                    }

                    if (values.eventDetails.endDate) {
                      eventDetails.endDate = fromZonedTime(
                        values.eventDetails.endDate,
                        values.eventDetails.timezone,
                      );
                    }

                    const { data: page, error } = await updatePage({
                      name: values.title || 'Untitled event',
                      title: values.title,
                      eventDetails: eventDetails,
                      type: 'event',
                      ...layoutDefaults,
                    });

                    if (error) throw error;

                    onCreatePage(page);
                  } catch (error) {
                    console.log(error);
                    logException({
                      message: 'Failed to create event',
                      error,
                    });
                    toast({
                      title: 'Unable to create event. Please try again.',
                      type: 'error',
                    });
                  }
                }}
                fields={({ values, setFieldValue }) => {
                  const isCapacityReached =
                    values.eventDetails?.capacity != null &&
                    values.eventDetails?.capacity != '' &&
                    values.eventDetails?.capacity === '0';

                  return [
                    {
                      name: 'title',
                      label: 'Event title',
                      type: 'text',
                      placeholder: 'Event title',
                      autoFocus: true,
                      required: true,
                    },
                    {
                      type: 'columns',
                      columns: 2,
                      fields: [
                        {
                          name: 'eventDetails.startDate',
                          label: 'Start date',
                          type: 'datetime-local',
                          required: !!values.eventDetails?.endDate,
                        },
                        {
                          name: 'eventDetails.endDate',
                          label: 'End date',
                          type: 'datetime-local',
                        },
                      ],
                    },
                    {
                      type: 'columns',
                      columns: 2,
                      fields: [
                        {
                          name: 'eventDetails.type',
                          label: 'Attendance',
                          type: 'select',
                          options: [
                            {
                              value: '',
                              label: 'Select event type',
                              disabled: true,
                            },
                            { value: 'in_person', label: 'In-person' },
                            { value: 'virtual', label: 'Virtual' },
                            { value: 'hybrid', label: 'Hybrid' },
                          ],
                        },
                        {
                          name: 'eventDetails.timezone',
                          required: false,
                          type: 'select',
                          label: 'Timezone',
                          tooltip: 'The timezone used for the event and in emails to attendees',
                          options: timezones.map(tz => ({
                            value: tz,
                            label: `${tz} (GMT ${getGmtOffsetForTimezone(tz)})`
                              .replace(/_/g, ' ')
                              .replace('/', ' - ')
                              .replace('Etc - UTC', 'UTC'),
                          })),
                        },
                      ],
                    },
                    {
                      name: 'eventDetails.location',
                      label: 'Location',
                      type: 'text',
                      visible:
                        values.eventDetails.type === 'in_person' ||
                        values.eventDetails.type === 'hybrid',
                    },
                    {
                      name: 'eventDetails.mapsLink',
                      label: 'Maps link',
                      type: 'url',
                      visible:
                        values.eventDetails.type === 'in_person' ||
                        values.eventDetails.type === 'hybrid',
                    },
                    {
                      name: 'eventDetails.platform',
                      label: 'Platform',
                      type: 'select',
                      options: [
                        { value: '', label: 'Select platform' },
                        { value: 'zoom', label: 'Zoom' },
                        { value: 'google_meet', label: 'Google Meet' },
                        { value: 'microsoft_teams', label: 'Microsoft Teams' },
                        { value: 'youtube', label: 'YouTube' },
                        { value: 'hopin', label: 'Hopin' },
                        { value: 'vimeo', label: 'Vimeo' },
                        { value: 'discord', label: 'Discord' },
                        { value: 'other', label: 'Other' },
                      ],
                      visible:
                        values.eventDetails.type === 'virtual' ||
                        values.eventDetails.type === 'hybrid',
                    },
                    {
                      name: 'eventDetails.meetingLink',
                      label: 'Meeting link',
                      type: 'url',
                      visible:
                        values.eventDetails.type === 'virtual' ||
                        values.eventDetails.type === 'hybrid',
                      placeholder: 'https://zoom.us/j/...',
                    },
                    {
                      type: 'columns',
                      columns: 2,
                      fields: [
                        {
                          name: 'eventDetails.status',
                          label: 'Event sales',
                          type: 'select',
                          options: [
                            { value: 'active', label: 'On sale', disabled: isCapacityReached },
                            { value: 'sold_out', label: 'Sold out' },
                            { value: 'canceled', label: 'Canceled' },
                          ],
                        },
                        {
                          name: 'eventDetails.capacity',
                          label: 'Max. capacity',
                          type: 'number',
                          onChange: e => {
                            setFieldValue('eventDetails.capacity', e.target?.value);
                            if (e.target?.value === '0') {
                              setFieldValue('eventDetails.status', 'sold_out');
                            }
                          },
                        },
                      ],
                    },
                    {
                      type: 'columns',
                      columns: 2,
                      fields: [
                        {
                          name: 'eventDetails.currency',
                          label: 'Currency',
                          type: 'currency',
                        },
                      ],
                      gap: 'sm',
                    },
                  ];
                }}
                submitButton={{
                  label: 'Continue',
                  size: 'full',
                  button: 'shadcn',
                  className: 'w-full',
                }}
                showState={false}
              />
            </Card>
          )}

          {pageType === 'form' && (
            <Form
              initialValues={{
                title: '',
              }}
              onSubmit={async values => {
                try {
                  const { data: page, error } = await updatePage({
                    name: values.title || 'Untitled form',
                    title: values.title,
                    type: 'form',
                    ...layoutDefaults,
                  });

                  if (error) throw error;

                  onCreatePage(page);
                } catch (error) {
                  console.log(error);
                  logException({
                    message: 'Failed to create form',
                    error,
                  });
                  toast({
                    title: 'Unable to create form. Please try again.',
                    type: 'error',
                  });
                }
              }}
              fields={[
                {
                  name: 'title',
                  label: 'Title',
                  type: 'text',
                  placeholder: 'Title',
                  optional: true,
                  autoFocus: true,
                },
              ]}
              submitButton={{
                label: 'Continue',
                size: 'full',
                button: 'shadcn',
                className: 'w-full',
              }}
              showState={false}
            />
          )}
        </div>
      </div>
    </>
  );
};

const CreateTicketTypes = ({ setNextStep }) => {
  return (
    <div className="mb-36">
      <div className="mx-auto max-w-lg">
        <div className="mb-8 text-center">
          <h1 className="text-xl text-balance">Create tickets</h1>
          <p className="text-muted-foreground text-sm">Add ticket types for your event</p>
        </div>

        <TicketGroupsProvider>
          <TicketGroupEditProvider>
            <CreateTicketTypesForm setNextStep={setNextStep} />
            <AddTicketGroupDialog />
          </TicketGroupEditProvider>
        </TicketGroupsProvider>
      </div>
    </div>
  );
};

function CreateTicketTypesForm({ setNextStep }) {
  const { state: ticketGroups } = useTicketGroups();
  const {
    isEditingTicketType,
    editingTicketGroupMode,
    editingTicketGroupId,
    isAddingTicketGroup,
    isAddingTicketType,
    hasUnsavedChanges,
  } = useTicketGroupEdit();

  return (
    <div className="flex flex-col">
      {/* Main ticket groups and types management */}
      <div>
        <TicketGroupsList isNewPageFlow={true} usePortal={true} />
      </div>

      {!isAddingTicketType &&
        !isEditingTicketType &&
        !isAddingTicketGroup &&
        !(editingTicketGroupId && editingTicketGroupMode === 'settings') &&
        ticketGroups.length > 0 && (
          <div className="flex justify-center mt-4">
            <ShadcnButton
              size="xl"
              className="w-full"
              onClick={async () => {
                let proceed = true;

                if (hasUnsavedChanges) {
                  proceed = await confirm('Discard unsaved changes?', {
                    confirm: 'Discard',
                  });
                }

                if (!proceed) return;

                setNextStep();
              }}
            >
              Continue
            </ShadcnButton>
          </div>
        )}
    </div>
  );
}

export default PageNew;
